import Header from '../../components/public/Header';
import Footer from '../../components/public/Footer';
import { Link, useNavigate } from 'react-router-dom';
import { useState } from 'react';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';
import { ArrowRight, CheckCircle2, Loader2, LockKeyhole, Mail, ShieldCheck } from 'lucide-react';
import API_BASE from '../../utils/apiBase';
import { currentPublicBrand } from '../../lib/publicBrand';
import { accessPrivacyCopy } from '../../lib/workforceAccess';

const normaliseWorkplaceCode = (value) => (
  (value || '').trim().toLowerCase().replace(/[^a-z0-9-]/g, '')
);

// One public entry screen. Existing authentication realms remain separate and
// are never selected by an unverified email or an applicant-provided role.
export default function AccessPortalPage() {
  const navigate = useNavigate();
  const brand = currentPublicBrand();
  const [workplaceCode, setWorkplaceCode] = useState('');
  const [email, setEmail] = useState('');
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [showAlternatives, setShowAlternatives] = useState(false);
  const [showWorkplaceFallback, setShowWorkplaceFallback] = useState(false);
  const savedWorkplaceSlug = typeof window !== 'undefined'
    ? localStorage.getItem('workerWorkspaceSlug')
    : '';

  const handleWorkplaceSubmit = (event) => {
    event.preventDefault();
    const safeCode = normaliseWorkplaceCode(workplaceCode);
    if (safeCode) navigate('/workforce/' + safeCode);
  };

  const handleEmailAccessSubmit = async (event) => {
    event.preventDefault();
    const address = email.trim();
    if (!address) {
      toast.error('Enter your work email address.');
      return;
    }

    setSending(true);
    try {
      // Existing worker/applicant access endpoint. NEVER use this request to
      // discover a role, workspace, client, or platform-owner privilege.
      await axios.post(API_BASE + '/worker/request-access', { email: address });
      setSent(true);
      toast.success('If this email is registered, a secure access link will arrive.');
    } catch {
      // Do not display account-specific server errors on a public entry page.
      toast.error('We could not process that request right now. Please try again later.');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className={brand.isCareDock
      ? "caredock-platform-theme min-h-screen bg-[#F3FAFF] text-[#0D274E] flex flex-col"
      : "min-h-screen bg-slate-50 text-slate-950 flex flex-col"}>
      <Header minimal />
      <main className="flex-1 flex items-center justify-center px-4 py-12 sm:py-20">
        <div className="w-full max-w-md">
          <div className="mb-8 text-center">
            {brand.isCareDock ? (
              <img src={brand.wordmark} alt="CareDock" className="mx-auto h-12 w-auto max-w-[220px]" />
            ) : (
              <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-2xl bg-primary text-white" aria-hidden="true">
                <span className="font-heading text-xl font-bold">C</span>
              </div>
            )}
            {!brand.isCareDock && <p className="mt-5 text-sm font-semibold tracking-wide text-primary">CareDock</p>}
            <h1 className="mt-2 font-heading text-3xl font-bold tracking-tight">Welcome back</h1>
            <p className="mt-3 text-sm leading-6 text-slate-600">
              Sign in to access your workplace.
            </p>
          </div>

          <form
            id="applicant-access"
            onSubmit={handleEmailAccessSubmit}
            className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm sm:p-8"
            data-testid="caredock-sign-in"
          >
            {sent ? (
              <div className="text-center space-y-4" role="status" aria-live="polite">
                <CheckCircle2 className="mx-auto h-10 w-10 text-primary" aria-hidden="true" />
                <h2 className="text-xl font-semibold">Check your email</h2>
                <p className="text-sm leading-6 text-slate-600">
                  If <strong>{email}</strong> is registered as an applicant or worker, we'll email a secure sign-in link.
                </p>
                <Button type="button" variant="outline" onClick={() => setSent(false)} className="w-full">
                  Use another email
                </Button>
              </div>
            ) : (
              <div className="space-y-5">
                <div>
                  <Label htmlFor="access-email" className="text-sm font-medium text-slate-800">
                    Work email address
                  </Label>
                  <div className="relative mt-2">
                    <Mail className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" aria-hidden="true" />
                    <Input
                      id="access-email"
                      type="email"
                      autoComplete="email"
                      required
                      value={email}
                      onChange={(event) => setEmail(event.target.value)}
                      placeholder="you@example.com"
                      className="h-12 pl-10 text-base"
                      data-testid="access-email-input"
                    />
                  </div>
                </div>
                <Button type="submit" disabled={sending} className="h-12 w-full bg-primary text-white hover:bg-primary-hover">
                  {sending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" /> : null}
                  Continue
                  {!sending ? <ArrowRight className="ml-2 h-4 w-4" aria-hidden="true" /> : null}
                </Button>
                <p className="text-center text-xs leading-5 text-slate-500">
                  For applicants and staff. If we recognise your email, you'll receive a private link. No password required.
                </p>
              </div>
            )}
          </form>

          <div className="mt-5 text-center">
            <button
              type="button"
              onClick={() => setShowAlternatives((open) => !open)}
              aria-expanded={showAlternatives}
              aria-controls="other-sign-in-methods"
              className="text-sm font-medium text-primary hover:underline"
            >
              {showAlternatives ? 'Hide other sign-in methods' : 'Other ways to sign in'}
            </button>
            {showAlternatives ? (
              <div id="other-sign-in-methods" className="mt-4 space-y-3 rounded-xl border border-slate-200 bg-white p-4 text-sm">
                <Link to="/login" className="block font-semibold text-slate-800 hover:text-primary">
                  I sign in with a password
                </Link>
                <Link to="/client/login" className="block font-semibold text-slate-800 hover:text-primary">
                  I manage a care home or client organisation
                </Link>
                <p className="text-xs leading-5 text-slate-500">
                  Your existing account and security checks still apply. This screen does not decide your access.
                </p>
              </div>
            ) : null}
          </div>

          <div className="mt-5 text-center">
            <button
              type="button"
              onClick={() => setShowWorkplaceFallback((open) => !open)}
              aria-expanded={showWorkplaceFallback}
              aria-controls="private-workplace-fallback"
              className="text-xs font-medium text-slate-600 hover:text-primary"
            >
              {showWorkplaceFallback ? 'Hide workplace link' : 'Have a private workplace link or code?'}
            </button>
            {showWorkplaceFallback ? (
              <form id="private-workplace-fallback" onSubmit={handleWorkplaceSubmit} className="mt-3 rounded-xl border border-slate-200 bg-white p-4 text-left">
                <Label htmlFor="workplace-code">Private workplace code</Label>
                <p className="mt-1 text-xs leading-5 text-slate-500">
                  Use only the code provided by your organisation. A code never grants access by itself.
                </p>
                <div className="mt-3 flex flex-col gap-2 sm:flex-row">
                  <Input
                    id="workplace-code"
                    value={workplaceCode}
                    onChange={(event) => setWorkplaceCode(event.target.value)}
                    placeholder="your-workplace-code"
                    data-testid="workplace-code-input"
                  />
                  <Button type="submit" className="shrink-0">Open</Button>
                </div>
                {savedWorkplaceSlug ? (
                  <Link to={'/workforce/' + normaliseWorkplaceCode(savedWorkplaceSlug)} className="mt-3 block text-xs font-medium text-primary">
                    Open previously used workplace
                  </Link>
                ) : null}
              </form>
            ) : null}
          </div>

          <div className="mt-8 flex items-start gap-2 rounded-xl border border-slate-200 p-4 text-xs leading-5 text-slate-600">
            <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-primary" aria-hidden="true" />
            <div>
              <p className="font-medium text-slate-800">Access is controlled by your organisation</p>
              <p>{accessPrivacyCopy({ publicPage: true })}</p>
            </div>
          </div>
          <div className="mt-4 flex justify-center">
            <LockKeyhole className="h-4 w-4 text-slate-400" aria-hidden="true" />
          </div>
        </div>
      </main>
      <Footer minimal />
    </div>
  );
}
